// The planning-topics catalog: every topic, grouped by planning area, with what it covers and
// the way into it. A topic the reader may not open shows no action.

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ApiFailure, type ApiClient } from '../api/client.js';
import type { TopicView } from '../api/contract.js';
import { topicPhrase } from '../format.js';
import { AREAS } from '../plan/areas.js';
import type { PlanState } from '../plan/usePlan.js';

export type CatalogPageProps = {
  state: PlanState;
  client: ApiClient;
  planId: string;
};

export function CatalogPage({
  state,
  client,
  planId,
}: CatalogPageProps): JSX.Element | null {
  const snapshot = state.snapshot;
  const navigate = useNavigate();
  const [problem, setProblem] = useState<string | null>(null);
  const [starting, setStarting] = useState<string | null>(null);
  if (snapshot === null) {
    return null;
  }

  const explore = async (topic: TopicView): Promise<void> => {
    if (topic.viewer.started) {
      navigate(`/plans/${planId}/topics/${topic.key}`);
      return;
    }
    setStarting(topic.key);
    setProblem(null);
    try {
      await client.openTopic(planId, topic.key);
      state.refetch();
      navigate(`/plans/${planId}/topics/${topic.key}`);
    } catch (error) {
      setProblem(
        error instanceof ApiFailure && error.code === 'refused'
          ? error.message
          : 'That didn’t start. Try again.',
      );
    } finally {
      setStarting(null);
    }
  };

  return (
    <section className="page home catalog">
      <Link className="catalog__back" to={`/plans/${planId}`}>
        ← Home
      </Link>
      <div>
        <h2 className="page__title home__title">Explore what you can plan for</h2>
        <p className="home__lede">
          Choose an area, see the kinds of decisions Mor can help with, and start where it
          feels relevant.
        </p>
      </div>
      {AREAS.map((area) => (
        <section key={area.title} className="home__section">
          <h3 className="home__heading">{area.title}</h3>
          <ul className="catalog__grid">
            {area.topics.map((key) => {
              const topic = snapshot.topics.find((candidate) => candidate.key === key);
              if (topic === undefined) {
                return null;
              }
              return (
                <li key={key} className="home__card catalog__card">
                  <span className="home__cardTitle">{topicPhrase(topic)}</span>
                  <span className="home__cardBody">{topic.description}</span>
                  <div className="catalog__examples">
                    <span className="catalog__eyebrow">For example</span>
                    {topic.examples.map((example) => (
                      <span key={example} className="catalog__example">
                        {example}
                      </span>
                    ))}
                  </div>
                  {topic.viewer.invited || topic.canStart ? (
                    <button
                      type="button"
                      className="home__talkLink home__doorLink catalog__action"
                      disabled={starting !== null}
                      onClick={() => void explore(topic)}
                    >
                      Explore with Mor →
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
      {problem === null ? null : (
        <p className="page__problem" role="alert">
          {problem}
        </p>
      )}
      {snapshot.plan.viewerIsCreator ? (
        <p className="home__talk catalog__foot">
          Not sure which applies?{' '}
          <Link className="home__talkLink" to={`/plans/${planId}/start`}>
            Help me get started →
          </Link>
        </p>
      ) : null}
    </section>
  );
}
